import { IArticle } from '~/utils/types/knowledgeTypes';
import Article from './Article/Article';

import styles from './ArticleList.module.scss';
import { Pagination } from '~/ui';
import { useState, useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { instance } from '~/utils/api/api';
import { useToastError } from '~/utils/useToastError';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
} from '@dnd-kit/sortable';

interface IArtikles {
  page?: number;
  setPage: (value: number) => void;
  data: IArticle[] | undefined;
  mobile?: boolean;
}

interface SelectedPage {
  selected: number;
}

const ArticleList = ({ data, page, setPage, mobile = false }: IArtikles) => {
  const toastError = useToastError();
  const client = useQueryClient();
  const [items, setItems] = useState<IArticle[]>(data ?? []);
  const wasDragging = useRef(false);

  useEffect(() => {
    setItems(data ?? []);
  }, [data]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const { mutate: saveSort } = useMutation(
    async (ids: number[]) => {
      const response = await instance.post('admin/knowledge-base-articles/sort', { ids });
      return response.data;
    },
    {
      onSuccess: () => {
        client.invalidateQueries(['knowledge-articles']);
      },
      onError: error => {
        toastError(error);
        setItems(data ?? []);
      },
    }
  );

  const handleDragEnd = (event: DragEndEvent) => {
    if (wasDragging.current) {
      const cancelClick = (e: MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        document.removeEventListener('click', cancelClick, true);
      };
      document.addEventListener('click', cancelClick, true);
      wasDragging.current = false;
    }

    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = items.findIndex(i => i.id === active.id);
    const newIndex = items.findIndex(i => i.id === over.id);
    const reordered = arrayMove(items, oldIndex, newIndex);
    setItems(reordered);
    saveSort(reordered.map(i => i.id));
  };

  const handleSetPage = (selectedPage: SelectedPage) => {
    setPage(selectedPage.selected + 1);
  };

  return (
    <div className={styles.listWrapper}>
      {mobile ? (
        <ul className={styles.mobileArticleList}>
          {items.map(item => (
            <Article data={item} key={item.id} mobile />
          ))}
        </ul>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={() => { wasDragging.current = true; }}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={items.map(i => i.id)} strategy={rectSortingStrategy}>
            <ul className={styles.articleList}>
              {items.map(item => (
                <Article data={item} key={item.id} wasDragging={wasDragging} />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      )}
      {page && !!items.length && (
        <Pagination pageCount={page} onPageChange={handleSetPage} />
      )}
    </div>
  );
};

export default ArticleList;
